<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" v-on:click.self="closeModal">
        <transition name="slide-in">
          <div v-if="showModal" class="modal-panel">
            <header class="modal-head">
              <div>
                <div class="modal-eyebrow">
                  <span class="status-dot"></span>
                  New user
                </div>
                <h2>Register User</h2>
                <p>Create a new account with a system role.</p>
              </div>
              <button type="button" class="icon-button" v-on:click="closeModal" aria-label="Close">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <!-- Plan seat limit reached: prompt to upgrade instead of the form. -->
            <div v-if="seatLimitReached" class="modal-form">
              <div class="modal-body">
                <section class="upgrade-section">
                  <div class="upgrade-icon"><i class="pi pi-lock"></i></div>
                  <h3>You've reached your team limit</h3>
                  <p>
                    Your current plan allows up to
                    <strong>{{ seatAllowance }}</strong> team member{{ seatAllowance === 1 ? "" : "s" }}.
                    Upgrade your subscription to add more users to your organization.
                  </p>
                </section>
              </div>
              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="closeModal">Cancel</button>
                <button type="button" class="primary-button" v-on:click="goToPlans">
                  <i class="pi pi-arrow-up-right"></i>
                  <span>View plans</span>
                </button>
              </footer>
            </div>

            <Form v-else class="modal-form" v-on:submit="registerUser">
              <div class="modal-body">
                <section class="form-section">
                  <h3>User Details</h3>
                  <div class="form-grid">
                    <label class="field">
                      <span>First Name</span>
                      <Field
                        type="text"
                        name="fname"
                        v-model="registerUserObj.fname"
                        placeholder="John"
                        :rules="{ required: ['First Name'] }"
                      />
                      <ErrorMessage name="fname" class="form-text text-danger" />
                    </label>

                    <label class="field">
                      <span>Last Name</span>
                      <Field
                        type="text"
                        name="lname"
                        v-model="registerUserObj.lname"
                        placeholder="Doe"
                        :rules="{ required: ['Last Name'] }"
                      />
                      <ErrorMessage name="lname" class="form-text text-danger" />
                    </label>

                    <label class="field full">
                      <span>Email</span>
                      <Field
                        type="email"
                        name="email"
                        v-model="registerUserObj.email"
                        placeholder="jon@example.com"
                        :rules="{ required: ['Email'], email: ['Email'] }"
                      />
                      <ErrorMessage name="email" class="form-text text-danger" />
                    </label>

                    <label class="field full">
                      <span>Phone Number</span>
                      <Field
                        type="tel"
                        name="phoneNumber"
                        v-model="registerUserObj.phoneNumber"
                        placeholder="+251..."
                      />
                    </label>

                    <label class="field full">
                      <span>Password</span>
                      <Field
                        type="password"
                        name="password"
                        v-model="registerUserObj.password"
                        placeholder="At least 6 characters"
                        :rules="{ required: ['Password'], min: [6] }"
                      />
                      <ErrorMessage name="password" class="form-text text-danger" />
                      <small class="hint">The member signs in with this password. They can change it later from their profile.</small>
                    </label>

                    <label class="field full">
                      <span>Role</span>
                      <select v-model="registerUserObj.role">
                        <option :value="null" disabled>Select role</option>
                        <option v-for="role in assignableRoles" :key="role" :value="role">{{ role }}</option>
                      </select>
                    </label>
                  </div>
                </section>
              </div>

              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="closeModal">Cancel</button>
                <button
                  type="submit"
                  class="primary-button"
                  :disabled="userIsLoading"
                >
                  <span v-if="!userIsLoading">Save Account</span>
                  <span v-else>Saving...</span>
                </button>
              </footer>
            </Form>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { Field, Form, ErrorMessage, defineRule } from "vee-validate";
import { email } from "@vee-validate/rules";
import { REGISTER_USER } from "@/store/action_types";
import { USER_ROLES } from "@/utils/enums";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { SUBSCRIPTION_ROUTE } from "@/router/routes";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

library.add(faXmark);

defineRule("email", email);
defineRule("required", (value, [fieldName = "Field"]) => {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
    return `${fieldName} is required`;
  }
  return true;
});
defineRule("min", (value, [limit]) => {
  if (value && value.length < Number(limit)) {
    return `Must be at least ${limit} characters`;
  }
  return true;
});

export default {
  components: { Field, Form, ErrorMessage },
  props: {
    // When true the org has used all the seats its plan allows; the modal shows
    // an upgrade prompt instead of the registration form.
    seatLimitReached: {
      type: Boolean,
      default: false,
    },
    seatAllowance: {
      type: Number,
      default: null,
    },
  },
  emits: ["close-add-user-modal", "refresh-users"],
  data() {
    return {
      // Only admin/user are honoured when creating a team member.
      assignableRoles: [USER_ROLES.admin, USER_ROLES.user],
      showModal: true,
      registerUserObj: {
        fname: null,
        lname: null,
        email: null,
        password: null,
        phoneNumber: null,
        role: USER_ROLES.user,
      },
    };
  },
  computed: {
    userIsLoading() {
      return this.$store.getters.user.isLoading;
    },
  },
  methods: {
    closeModal() {
      this.$emit("close-add-user-modal");
    },
    goToPlans() {
      this.closeModal();
      this.$router.push({ name: SUBSCRIPTION_ROUTE });
    },
    registerUser() {
      const payload = {
        fname: this.registerUserObj.fname,
        lname: this.registerUserObj.lname,
        email: this.registerUserObj.email,
        phoneNumber: this.registerUserObj.phoneNumber,
        password: this.registerUserObj.password,
        role: this.registerUserObj.role,
      };

      this.$store.dispatch(REGISTER_USER, payload).then((res) => {
        if (res) {
          this.$toast.success("Account created.", TOAST_GLOBAL_CONFIG);
          this.$emit("refresh-users");
          this.closeModal();
        } else {
          // Surface the server reason (e.g. the 402 plan/seat-limit message).
          this.$toast.error(
            this.$store.getters.errorMsg || "Could not create the account.",
            TOAST_GLOBAL_CONFIG,
          );
        }
      });
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal-panel {
  --dashboard-blue: #73c41d;
  --dashboard-blue-dark: #538c13;
  --dashboard-blue-soft: #eef6df;
  --dashboard-blue-border: #cfe6a3;
  --dashboard-slate: #34404a;
  --dashboard-muted: #76828b;
  --dashboard-border: #e3e8e6;
  --dashboard-canvas: #f4f6f3;
  --dashboard-green: #4aa12a;
  --dashboard-red: #e0464d;
  width: 480px;
  max-width: 95vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid var(--dashboard-border);
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
  color: var(--dashboard-slate);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--dashboard-border);
}

.modal-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px;
  margin-bottom: 8px;
  border: 1px solid var(--dashboard-border);
  border-radius: var(--ehs-radius-pill);
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--dashboard-green);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12);
}

.modal-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.modal-head p {
  margin: 4px 0 0;
  color: var(--dashboard-muted);
  font-size: 0.86rem;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--dashboard-border);
  background: #ffffff;
  border-radius: 50%;
  color: var(--dashboard-slate);
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.icon-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.modal-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 32px;
  background: var(--dashboard-canvas);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-section {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.form-section h3 {
  margin: 0 0 14px;
  font-family: var(--ehs-font-display);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field.full {
  grid-column: 1 / -1;
}

.field > span {
  color: var(--dashboard-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.field input,
.field select,
.field textarea {
  padding: 10px 12px;
  border: 1px solid var(--dashboard-border);
  border-radius: 10px;
  background: #ffffff;
  color: var(--dashboard-slate);
  font-size: 0.86rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  border-color: var(--dashboard-blue);
  box-shadow: 0 0 0 3px rgba(115, 196, 29, 0.18);
}

.form-text.text-danger {
  margin-top: 4px;
  color: var(--dashboard-red);
  font-size: 0.74rem;
  font-weight: 500;
  position: relative;
  left: 4px;
}

.segmented {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 4px;
  background: var(--dashboard-canvas);
  border: 1px solid var(--dashboard-border);
  border-radius: 12px;
}

.segmented button {
  padding: 9px 12px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: var(--dashboard-muted);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.segmented button.active {
  background: #ffffff;
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06) !important;
}

.hint {
  margin-top: 6px;
  color: var(--dashboard-muted);
  font-size: 0.74rem;
  line-height: 1.5;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}

.upgrade-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 30px 22px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.upgrade-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  margin-bottom: 6px;
  border-radius: 50%;
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  font-size: 1.3rem;
}

.upgrade-section h3 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--dashboard-slate);
}

.upgrade-section p {
  margin: 0;
  max-width: 320px;
  color: var(--dashboard-muted);
  font-size: 0.86rem;
  line-height: 1.55;
}

.upgrade-section p strong {
  color: var(--dashboard-slate);
}

.primary-button .pi {
  font-size: 0.72rem;
  margin-right: 2px;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--dashboard-border);
  background: #ffffff;
}

.ghost-button,
.primary-button {
  border-radius: var(--ehs-radius-pill);
  border: 1px solid transparent;
  padding: 10px 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.ghost-button {
  background: #ffffff;
  color: var(--dashboard-slate);
  border-color: var(--dashboard-border);
}

.ghost-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.primary-button {
  background: var(--dashboard-blue);
  color: #ffffff;
  border-color: var(--dashboard-blue);
}

.primary-button:hover {
  background: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-dark);
}

.primary-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (max-width: 991.98px) {
  .modal-panel {
    width: 100%;
    max-width: 100%;
  }

  .modal-head {
    padding: 16px 16px 14px;
    gap: 10px;
  }

  .modal-head h2 {
    font-size: 1.2rem;
  }

  .modal-head p {
    font-size: 0.8rem;
  }

  .modal-body {
    padding: 16px 14px 24px;
  }

  .form-section {
    padding: 14px 14px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-foot {
    padding: 12px 14px;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .modal-foot .ghost-button,
  .modal-foot .primary-button {
    width: 100%;
  }
}
</style>
